/**
 * 为背景图片按显示尺寸采样，仅保留当前目标尺寸的一张结果图
 * 由背景图层绘制入口调用，重新采样时交替使用两张临时画布并在结束后释放
 */

/** 为背景图片保留当前显示尺寸的采样结果，避免细密纹理缩小时产生摩尔纹 */
export class BackgroundImageSampler {
    private source: HTMLCanvasElement | null = null;
    private sourceWidth = 0;
    private sourceHeight = 0;
    private sample: HTMLCanvasElement | null = null;

    /**
     * 复用适合当前像素尺寸的结果，不匹配时重新采样并替换唯一结果图
     *
     * @param ctx - 已应用图层变换和透明度的绘制上下文
     * @param source - 已就绪的背景图片画布；原地重绘后须先调用 clear
     * @param width - 图片的逻辑绘制宽度
     * @param height - 图片的逻辑绘制高度
     * 副作用：绘制背景图片，按需更新当前结果并释放全部中间采样缓冲
     */
    draw(ctx: CanvasRenderingContext2D, source: HTMLCanvasElement, width: number, height: number): void {
        const transform = ctx.getTransform();
        // 变换已包含 DPR 和旋转；非等比缩放保留较清晰的一轴。
        const scale = Math.max(
            (Math.abs(width) * Math.hypot(transform.a, transform.b)) / source.width,
            (Math.abs(height) * Math.hypot(transform.c, transform.d)) / source.height,
        );
        if (!Number.isFinite(scale) || scale <= 0 || scale >= 1) {
            this.clear();
            ctx.drawImage(source, 0, 0, width, height);
            return;
        }
        const targetWidth = Math.max(1, Math.ceil(source.width * scale));
        const targetHeight = Math.max(1, Math.ceil(source.height * scale));
        if (targetWidth === source.width && targetHeight === source.height) {
            this.clear();
            ctx.drawImage(source, 0, 0, width, height);
            return;
        }
        if (
            source !== this.source ||
            source.width !== this.sourceWidth ||
            source.height !== this.sourceHeight ||
            this.sample?.width !== targetWidth ||
            this.sample.height !== targetHeight
        ) {
            this.resample(source, targetWidth, targetHeight);
            this.source = source;
            this.sourceWidth = source.width;
            this.sourceHeight = source.height;
        }
        ctx.drawImage(this.sample!, 0, 0, width, height);
    }

    /**
     * 在背景资源重建或图层销毁时释放当前结果
     * 副作用：清除源引用并释放唯一采样结果的像素缓冲
     */
    clear(): void {
        if (this.sample) {
            this.sample.width = 0;
            this.sample.height = 0;
        }
        this.sample = null;
        this.source = null;
        this.sourceWidth = 0;
        this.sourceHeight = 0;
    }

    private resample(source: HTMLCanvasElement, targetWidth: number, targetHeight: number): void {
        const temporary: HTMLCanvasElement[] = [];
        const create = () => {
            const canvas = document.createElement('canvas');
            temporary.push(canvas);
            return canvas;
        };
        const resize = (canvas: HTMLCanvasElement, w: number, h: number) => {
            canvas.width = w;
            canvas.height = h;
            const context = canvas.getContext('2d')!;
            context.imageSmoothingEnabled = true;
            // 每级最多缩小一半，用双线性平均，不依赖浏览器对 high 提示的实现。
            context.imageSmoothingQuality = 'low';
            return context;
        };

        try {
            const rasterScale = Math.max(targetWidth, targetHeight) / Math.max(source.width, source.height);
            const level = Math.log2(1 / rasterScale);
            const upperLevel = Math.floor(level);
            const lowerWeight = level - upperLevel;
            const steps = upperLevel + (lowerWeight > 0 ? 1 : 0);
            const buffers: HTMLCanvasElement[] = [];
            let upper = source;
            let lower = source;
            for (let i = 1; i <= steps; i++) {
                if (lower.width === 1 && lower.height === 1) break;
                upper = lower;
                // 两张临时画布轮流覆盖上一层，不保存 N 个采样层级。
                const index = (i - 1) % 2;
                const next = (buffers[index] ??= create());
                const samplingCtx = resize(
                    next,
                    Math.max(1, Math.ceil(upper.width / 2)),
                    Math.max(1, Math.ceil(upper.height / 2)),
                );
                samplingCtx.drawImage(upper, 0, 0, next.width, next.height);
                lower = next;
            }
            if (lowerWeight === 0) upper = lower;

            const result = this.sample ?? document.createElement('canvas');
            this.sample = result;
            const resultCtx = resize(result, targetWidth, targetHeight);
            resultCtx.globalAlpha = 1 - lowerWeight;
            resultCtx.drawImage(upper, 0, 0, targetWidth, targetHeight);
            if (lowerWeight > 0) {
                // 预乘颜色与 alpha 分别相加，避免半透明背景在混合后变淡。
                resultCtx.globalCompositeOperation = 'lighter';
                resultCtx.globalAlpha = lowerWeight;
                resultCtx.drawImage(lower, 0, 0, targetWidth, targetHeight);
            }
        } catch (error) {
            this.clear();
            throw error;
        } finally {
            for (const canvas of temporary) {
                canvas.width = 0;
                canvas.height = 0;
            }
        }
    }
}
